import React, { useCallback, useMemo } from 'react';
import { Button, Space, Collapse, Alert, Divider, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined, CaretRightOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Title } = Typography;

const WorkflowChangesApproval = ({ 
  changes = {},
  onApprove,
  onReject,
  loading = false,
  colorMode = 'light'
}) => {
  // Memoize the formatted changes to prevent unnecessary recalculations
  const formattedChanges = useMemo(() => {
    if (!changes) return [];
    
    const formatted = [];
    
    if (changes.modified?.length > 0) {
      formatted.push({
        type: 'modified',
        nodes: changes.modified
      });
    }
    
    if (changes.added?.length > 0) {
      formatted.push({
        type: 'added',
        nodes: changes.added
      });
    }
    
    if (changes.removed?.length > 0) {
      formatted.push({
        type: 'removed',
        nodes: changes.removed
      });
    }

    if (changes.connections?.length > 0) {
      formatted.push({
        type: 'connections',
        changes: changes.connections
      });
    }
    
    return formatted;
  }, [changes]);

  // Memoize handlers
  const handleApprove = useCallback(() => {
    if (onApprove) onApprove();
  }, [onApprove]);

  const handleReject = useCallback(() => {
    if (onReject) onReject();
  }, [onReject]);

  // Function to highlight differences
  const highlightDiff = (before, after) => {
    if (!before || !after) return null;
    
    // Simple implementation to highlight changes
    const beforeStr = JSON.stringify(before, null, 2);
    const afterStr = JSON.stringify(after, null, 2);
    
    // Create an array of lines
    const beforeLines = beforeStr.split('\n');
    const afterLines = afterStr.split('\n');
    
    // Render after with highlighted changes
    return afterLines.map((line, i) => {
      const beforeLine = beforeLines[i] || '';
      
      // If lines are different, highlight
      if (line !== beforeLine) {
        return (
          <div key={i} className="diff-line modified">
            {line}
          </div>
        );
      }
      
      return (
        <div key={i} className="diff-line">
          {line}
        </div>
      );
    });
  };

  return (
    <div className="workflow-changes">
      <Alert
        message="Suggested Workflow Changes"
        description="I can make the following improvements to your workflow:"
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ marginBottom: '16px' }}
      />
      
      <Collapse 
        defaultActiveKey={['0']}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        {formattedChanges.map((changeGroup, index) => (
          <Panel 
            header={
              changeGroup.type === 'modified' ? 'Modified Nodes' :
              changeGroup.type === 'added' ? 'Added Nodes' :
              changeGroup.type === 'removed' ? 'Removed Nodes' : 
              'Connection Changes'
            }
            key={index}
          >
            {changeGroup.type === 'modified' && changeGroup.nodes.map(node => (
              <div key={node.id} className="change-item">
                <Title level={5}>Node: {node.id}</Title>
                
                {/* Before state - Full width */}
                <div className="state-container">
                  <div className="state-header">Before:</div>
                  <div className="json-container">
                    <pre className="json-code">
                      {JSON.stringify(node.before, null, 2)}
                    </pre>
                  </div>
                </div>
                
                {/* Divider between states */}
                <Divider style={{ margin: '16px 0' }} />
                
                {/* After state - Full width with highlights */}
                <div className="state-container">
                  <div className="state-header">After:</div>
                  <div className="json-container">
                    <div className="json-code with-diff">
                      {highlightDiff(node.before, node.after)}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {changeGroup.type === 'added' && changeGroup.nodes.map(node => (
              <div key={node.id} className="change-item">
                <Title level={5}>New Node: {node.type}</Title>
                <div className="json-container">
                  <pre className="json-code">
                    {JSON.stringify(node.data, null, 2)}
                  </pre>
                </div>
              </div>
            ))}

            {changeGroup.type === 'removed' && changeGroup.nodes.map(nodeId => (
              <div key={nodeId} className="change-item">
                <Title level={5}>Removed Node: {nodeId}</Title>
              </div>
            ))}

            {changeGroup.type === 'connections' && (
              <div className="change-item">
                <Title level={5}>Connection Changes:</Title>
                <div className="json-container">
                  <pre className="json-code">
                    {JSON.stringify(changeGroup.changes, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </Collapse>

      <div className="action-buttons">
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleApprove}
            loading={loading}
          >
            Apply Changes
          </Button>
          <Button
            icon={<CloseOutlined />}
            onClick={handleReject}
            disabled={loading}
          >
            Reject
          </Button>
        </Space>
      </div>

      <style jsx>{`
        .workflow-changes {
          margin: 16px 0;
          background: ${colorMode === 'dark' ? '#1f1f1f' : '#fff'};
          border-radius: 8px;
          overflow: hidden;
          max-width: 100%;
        }

        .change-item {
          margin: 16px 0;
          padding: 16px;
          background: ${colorMode === 'dark' ? '#141414' : '#f5f5f5'};
          border-radius: 6px;
        }

        .state-container {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        
        .state-header {
          font-weight: bold;
          margin-bottom: 8px;
          color: ${colorMode === 'dark' ? '#e6e6e6' : '#333'};
        }

        .json-container {
          max-height: 300px;
          overflow: auto;
          background: ${colorMode === 'dark' ? '#000' : '#fff'};
          border-radius: 6px;
          padding: 12px;
          width: 100%;
        }

        .json-code {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
          font-family: monospace;
          font-size: 12px;
          color: ${colorMode === 'dark' ? '#e6e6e6' : '#333'};
        }
        
        .json-code.with-diff {
          font-family: monospace;
        }
        
        .diff-line {
          padding: 2px 0;
          line-height: 1.5;
        }
        
        .diff-line.modified {
          background-color: ${colorMode === 'dark' ? 'rgba(24, 144, 255, 0.2)' : 'rgba(24, 144, 255, 0.1)'};
          border-left: 3px solid #1890ff;
          padding-left: 4px;
        }

        .action-buttons {
          margin-top: 16px;
          display: flex;
          justify-content: flex-end;
          padding: 16px;
          background: ${colorMode === 'dark' ? '#141414' : '#f5f5f5'};
          border-radius: 0 0 8px 8px;
        }
      `}</style>
    </div>
  );
};

export default React.memo(WorkflowChangesApproval);